'use client';

import Link from 'next/link';
import { useState, useRef, useEffect } from 'react';
import {
  Send, Bot, User, Loader2, Sparkles, ArrowRight,
  MessageSquare, Image as ImageIcon, FileText, FileSpreadsheet, Mic, Film, Users, Store,
} from 'lucide-react';
import { getTrialState, saveTrialState, clearTrialState, onTrialStateChange, MAX_FREE_MESSAGES, type TrialMessage } from '@/lib/trial-store';
import { CURRENT_PHASE } from '@/lib/pricing-phase';
import { FREE_TIER } from '@/lib/pricing-config';
import { renderMarkdown } from '@/components/chat/message-bubble';

/* ─────────────────────────────────────────────
   LandingTrial — anonymous landing experience.
   Home mode: centered textarea + quick prompts + tool tiles (matches -main vibe).
   Chat mode: messages + input + counter, activates on first send.
   Proxy: /api/trial/chat — if degraded, renders the "I'm offline" reply gracefully.
   Persistence: sessionStorage via trial-store — lets the chat drawer on other pages show the same conversation.
   ───────────────────────────────────────────── */

type Message = TrialMessage;

// Anonymous-friendly only. Every prompt here must be answerable WITHOUT
// account context (no "your data", no business connection). Brandon's
// 2026-05-12 feedback after the previous list shipped "What's my revenue
// this month?" — an unresolvable question that wasted message #1 telling
// the user to sign up.
const QUICK_PROMPTS = [
  "I run a lawn care business and need more leads",
  "Draft a client follow-up email",
  "Generate an image of a modern office",
  "Write a 3-message follow-up sequence for a missed sales call",
];

type ToolId = 'chat' | 'docs' | 'image' | 'sheets' | 'video' | 'audio' | 'agents' | 'marketplace';

interface Tool { id: ToolId; name: string; icon: React.ComponentType<{ className?: string }>; color: string; prompt?: string; link?: string; gated?: boolean }

// Anonymous landing tile set. Slides and Dev (code gen / build-a-website/app)
// are intentionally NOT shown here: they're paid Implementation Services and
// need to be inquired about via /marketplace-public, not free-tier prompts.
const TOOLS: Tool[] = [
  { id: 'chat',        name: 'AI Chat',        icon: MessageSquare,   color: '#6366f1' },
  { id: 'image',       name: 'AI Image',       icon: ImageIcon,       color: '#ec4899', prompt: 'Generate an image of ' },
  { id: 'docs',        name: 'AI Docs',        icon: FileText,        color: '#3b82f6', prompt: 'Write an email about ' },
  { id: 'sheets',      name: 'AI Sheets',      icon: FileSpreadsheet, color: '#10b981', prompt: 'Build a spreadsheet for ' },
  { id: 'video',       name: 'AI Video',       icon: Film,            color: '#ef4444', prompt: 'Storyboard a video about ', gated: true },
  { id: 'audio',       name: 'AI Audio',       icon: Mic,             color: '#14b8a6', prompt: 'Draft an audio script for ', gated: true },
  { id: 'agents',      name: 'All Agents',     icon: Users,           color: '#f97316', link: '/marketplace-public' },
  { id: 'marketplace', name: 'Marketplace',    icon: Store,           color: '#a855f7', link: '/marketplace-public' },
];

export function LandingTrial() {
  const [messages, setMessages]                 = useState<Message[]>([]);
  const [input, setInput]                       = useState('');
  const [isTyping, setIsTyping]                 = useState(false);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const scrollRef        = useRef<HTMLDivElement>(null);
  const sessionIdRef     = useRef<string>('');
  const textareaRef      = useRef<HTMLTextAreaElement>(null);

  // Hydrate from sessionStorage + subscribe to cross-component updates (drawer <-> landing).
  useEffect(() => {
    const state = getTrialState();
    sessionIdRef.current = state.sessionId;
    if (state.messages.length > 0) {
      setMessages(state.messages);
      setUserMessageCount(state.userMessageCount);
    }
    const unsub = onTrialStateChange(next => {
      sessionIdRef.current = next.sessionId;
      setMessages(next.messages);
      setUserMessageCount(next.userMessageCount);
    });
    return unsub;
  }, []);

  // Bootstrap the trial cookie on mount so workspace-main can tie any
  // conversations + generated artifacts to a trial_session row. Without this
  // the landing chat still works, but artifacts created here have no rebind
  // key and will not carry over into /artifacts after signup. See
  // docs/superpowers/specs/2026-04-21-signup-seam-design.md.
  //
  // Drift guard: compare the trialId returned by /api/trial/init against the
  // last one we saw. If they differ AND we have local messages, the cookie
  // has rotated (expired past the 7-day window or was cleared) — sessionStorage
  // is now pinned to a dead trial_session row. Wipe it so the visitor starts
  // a clean session instead of seeing ghost messages that can't be continued.
  // Spec: docs/superpowers/specs/2026-05-12-landing-trial-funnel-design.md (item #10).
  useEffect(() => {
    fetch('/api/trial/init', { method: 'GET', credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then((data: { trialId?: string } | null) => {
        if (!data?.trialId) return;
        const current = getTrialState();
        const drifted =
          current.lastKnownTrialId &&
          current.lastKnownTrialId !== data.trialId &&
          current.messages.length > 0;
        if (drifted) {
          clearTrialState();
          setMessages([]);
          setUserMessageCount(0);
          sessionIdRef.current = '';
        }
        // Persist the fresh trialId so the next mount can detect drift.
        // Preserves any other state the user has in flight.
        const next = drifted ? { sessionId: '', messages: [], userMessageCount: 0 } : current;
        saveTrialState({ ...next, lastKnownTrialId: data.trialId });
      })
      .catch(() => { /* silent — init failure shouldn't block the landing UX */ });
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isTyping]);

  const walled    = userMessageCount >= MAX_FREE_MESSAGES;
  const remaining = Math.max(0, MAX_FREE_MESSAGES - userMessageCount);
  const inChatMode = messages.length > 0;

  // Image gen via MiniMax routinely takes 45-55s. A bare spinner for that long
  // reads as "the page is broken" — surface the expected latency in the typing
  // indicator when the user's most recent message looks image-shaped.
  const lastUserMessage = [...messages].reverse().find(m => m.role === 'user')?.content ?? '';
  const isImageRequest = /\b(image|picture|photo|draw|render|logo|illustration)\b/i.test(lastUserMessage);

  async function sendMessage(text: string) {
    const clean = text.trim();
    if (!clean || isTyping || walled) return;

    const userMsg: Message = { id: crypto.randomUUID(), role: 'user', content: clean };
    const nextMessages = [...messages, userMsg];
    const nextCount = userMessageCount + 1;
    setMessages(nextMessages);
    setUserMessageCount(nextCount);
    saveTrialState({ sessionId: sessionIdRef.current, messages: nextMessages, userMessageCount: nextCount });
    setInput('');
    setIsTyping(true);

    try {
      const res = await fetch('/api/trial/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: clean, session_id: sessionIdRef.current || undefined }),
      });
      const data: { reply?: string; session_id?: string; degraded?: boolean } = await res.json().catch(() => ({}));

      // Server returns '' when our cached id is stale and the next turn
      // should start fresh. Overwrite with either the new id or clear it
      // so we don't loop on a dead conversation.
      sessionIdRef.current = data.session_id ?? '';

      const assistantMsg: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.reply ?? "Sorry, I didn't catch that — could you rephrase?",
        degraded: !!data.degraded,
      };
      const finalMessages = [...nextMessages, assistantMsg];
      setMessages(finalMessages);
      saveTrialState({ sessionId: sessionIdRef.current, messages: finalMessages, userMessageCount: nextCount });
    } catch {
      const errMsg: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: "I'm offline for a moment — try again, or sign up to go deeper.",
        degraded: true,
      };
      const finalMessages = [...nextMessages, errMsg];
      setMessages(finalMessages);
      saveTrialState({ sessionId: sessionIdRef.current, messages: finalMessages, userMessageCount: nextCount });
    } finally {
      setIsTyping(false);
    }
  }

  function handleSubmit() { void sendMessage(input); }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  function handleToolClick(tool: Tool) {
    if (tool.link) return; // handled by Link
    if (tool.prompt) {
      setInput(tool.prompt);
      textareaRef.current?.focus();
    }
  }

  function handleQuickPrompt(prompt: string) {
    setInput(prompt);
    textareaRef.current?.focus();
  }

  return (
    <div id="hero-chat" className="w-full">
      {/* HOME MODE — visible until first message */}
      {!inChatMode && (
        <>
          {/* Centered chat input */}
          <div className="relative mx-auto max-w-3xl">
            <div className="rounded-3xl bg-surface-container ghost-border p-2 shadow-ambient-lg focus-within:ring-1 focus-within:ring-primary/40 transition-all">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask anything about your business…"
                rows={3}
                className="w-full resize-none bg-transparent px-5 py-3 text-on-surface placeholder:text-on-surface-muted text-base leading-relaxed outline-none min-h-[72px] max-h-[160px]"
              />
              <div className="flex items-center justify-between px-3 pb-2">
                <p className="text-[11px] uppercase tracking-widest text-on-surface-muted flex items-center gap-2">
                  <Sparkles className="size-3 text-primary-bright" />
                  5 free messages — no sign-up
                </p>
                <button
                  onClick={handleSubmit}
                  disabled={!input.trim() || isTyping}
                  aria-label="Send message"
                  className="size-10 rounded-xl gradient-primary flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed hover:brightness-110 transition-all shadow-[0_0_12px_oklch(0.55_0.25_285_/_0.3)]"
                >
                  <Send className="size-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Quick-prompt chips */}
          <div className="mx-auto max-w-3xl mt-4 flex flex-wrap gap-2 justify-center">
            {QUICK_PROMPTS.map(q => (
              <button
                key={q}
                onClick={() => handleQuickPrompt(q)}
                className="px-3.5 py-1.5 rounded-full text-xs font-medium bg-surface-container text-on-surface-variant hover:bg-surface-high hover:text-on-surface ghost-border transition-colors"
              >
                {q}
              </button>
            ))}
          </div>

          {/* Tool tile grid */}
          <div className="mx-auto max-w-4xl mt-10">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-8 gap-1">
              {TOOLS.map(tool => {
                const Icon = tool.icon;
                const content = (
                  <>
                    <div
                      className="size-12 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110"
                      style={{ background: `${tool.color}18`, color: tool.color }}
                    >
                      <Icon className="size-5" />
                    </div>
                    <span className="text-[11px] font-medium text-on-surface group-hover:text-on-surface transition-colors">
                      {tool.name}
                    </span>
                    {tool.gated && (
                      <span className="absolute top-1 right-1 text-[9px] font-bold uppercase tracking-widest text-on-surface-muted bg-surface-bright px-1.5 py-0.5 rounded">Pro</span>
                    )}
                  </>
                );
                const baseClass = 'group relative flex flex-col items-center gap-2 p-3 rounded-2xl hover:bg-surface-container transition-colors';
                return tool.link ? (
                  <Link key={tool.id} href={tool.link} className={baseClass}>{content}</Link>
                ) : (
                  <button
                    key={tool.id}
                    onClick={() => handleToolClick(tool)}
                    disabled={tool.gated && walled}
                    className={baseClass}
                  >
                    {content}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* CHAT MODE — activates once messages exist */}
      {inChatMode && (
        <div className="mx-auto max-w-3xl space-y-3">
          <div className="flex items-center justify-between px-1 text-xs text-on-surface-muted">
            <span className="inline-flex items-center gap-1.5">
              <span className="size-1.5 rounded-full bg-primary animate-pulse" />
              {walled ? 'Free messages used' : `${remaining} of ${MAX_FREE_MESSAGES} free messages left`}
            </span>
            <span className="uppercase tracking-widest">Anonymous</span>
          </div>

          <div className="rounded-2xl bg-surface-container ghost-border overflow-hidden shadow-ambient">
            <div ref={scrollRef} className="max-h-[420px] overflow-y-auto p-4 space-y-3">
              {messages.map(msg => (
                <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="shrink-0 size-7 rounded-lg gradient-primary flex items-center justify-center mt-0.5">
                      <Bot className="size-4 text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-primary text-white rounded-tr-sm whitespace-pre-line'
                        : 'bg-surface-high text-on-surface rounded-tl-sm'
                    }`}
                  >
                    {/* Note: `msg.degraded` is still set by the upstream proxy
                        and logged for diagnostics, but we don't render a
                        "DEGRADED" pill to anonymous visitors — for non-
                        engineers it reads as "this product is broken." The
                        fallback reply copy carries enough context on its own. */}
                    {msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content}
                  </div>
                  {msg.role === 'user' && (
                    <div className="shrink-0 size-7 rounded-lg bg-surface-bright flex items-center justify-center mt-0.5">
                      <User className="size-4 text-on-surface-variant" />
                    </div>
                  )}
                </div>
              ))}
              {isTyping && (
                <div className="flex gap-3 justify-start">
                  <div className="shrink-0 size-7 rounded-lg gradient-primary flex items-center justify-center mt-0.5">
                    <Bot className="size-4 text-white" />
                  </div>
                  <div className="bg-surface-high rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm text-on-surface-muted inline-flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin shrink-0" />
                    <span>
                      {isImageRequest
                        ? 'Working on it — image generation can take up to a minute'
                        : 'Thinking…'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-end gap-2 p-3 border-t border-[var(--outline)]">
              <div className="flex-1 flex items-center gap-3 rounded-xl bg-surface-high px-4 py-2.5">
                <Sparkles className="size-4 text-primary-bright shrink-0" />
                <input
                  type="text"
                  value={input}
                  disabled={walled || isTyping}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={walled ? 'Sign up to keep going' : isTyping ? 'Thinking…' : 'Keep the conversation going…'}
                  className="flex-1 bg-transparent text-on-surface placeholder:text-on-surface-muted text-sm outline-none disabled:opacity-60"
                />
              </div>
              <button
                onClick={handleSubmit}
                disabled={!input.trim() || isTyping || walled}
                className="shrink-0 size-10 rounded-xl gradient-primary flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed hover:brightness-110 transition-all shadow-[0_0_12px_oklch(0.55_0.25_285_/_0.3)]"
              >
                <Send className="size-4" />
              </button>
            </div>
          </div>

          {/* Phase-aware signup wall. Drives off CURRENT_PHASE (one switch
              for hero subhead, wall CTA, and top-nav CTA) instead of the
              old SIGNUP_REQUIRES_PROMO env flag — the env flag stays in
              use on /signup itself but no longer fights this surface.
              Spec: docs/superpowers/specs/2026-05-12-landing-trial-funnel-design.md
              (item #6). */}
          {walled && (
            <div className="rounded-2xl bg-surface-container ghost-border p-6 shadow-ambient">
              <div className="flex items-start gap-4 mb-5">
                <div className="shrink-0 size-10 rounded-lg gradient-primary flex items-center justify-center">
                  <Sparkles className="size-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-on-surface mb-1">
                    {CURRENT_PHASE === 'pre_alpha' ? 'Pre-alpha demo' : 'Keep the conversation going'}
                  </h3>
                  <p className="text-sm text-on-surface-variant">
                    {CURRENT_PHASE === 'pre_alpha' && (
                      <>We&rsquo;re in pre-alpha right now. Request an invite to keep going — we&rsquo;ll save this chat.</>
                    )}
                    {(CURRENT_PHASE === 'alpha' || CURRENT_PHASE === 'beta' || CURRENT_PHASE === 'waitlist') && (
                      <>You&rsquo;ve seen what it can do. Join the waitlist or enter your cohort code to continue — we&rsquo;ll save this chat.</>
                    )}
                    {(CURRENT_PHASE === 'intro' || CURRENT_PHASE === 'ga') && (
                      <>Create a free account — <strong className="text-on-surface">{FREE_TIER.days} days, {FREE_TIER.messages} agent messages, {FREE_TIER.images} images, {FREE_TIER.videos} video, no card</strong>. We&rsquo;ll save this chat.</>
                    )}
                  </p>
                </div>
              </div>

              {/* Primary CTA */}
              {CURRENT_PHASE === 'pre_alpha' && (
                <Link
                  href="/pricing#waitlist"
                  className="inline-flex items-center justify-center gap-2 w-full gradient-primary text-white font-semibold px-5 py-3 rounded-lg text-sm hover:brightness-110 transition-all shadow-[0_0_16px_oklch(0.55_0.25_285_/_0.25)]"
                >
                  Request invite
                  <ArrowRight className="size-4" />
                </Link>
              )}
              {(CURRENT_PHASE === 'alpha' || CURRENT_PHASE === 'beta' || CURRENT_PHASE === 'waitlist') && (
                <Link
                  href="/pricing#waitlist"
                  className="inline-flex items-center justify-center gap-2 w-full gradient-primary text-white font-semibold px-5 py-3 rounded-lg text-sm hover:brightness-110 transition-all shadow-[0_0_16px_oklch(0.55_0.25_285_/_0.25)]"
                >
                  Join the waitlist
                  <ArrowRight className="size-4" />
                </Link>
              )}
              {(CURRENT_PHASE === 'intro' || CURRENT_PHASE === 'ga') && (
                <Link
                  href="/signup?from=trial"
                  className="inline-flex items-center justify-center gap-2 w-full gradient-primary text-white font-semibold px-5 py-3 rounded-lg text-sm hover:brightness-110 transition-all shadow-[0_0_16px_oklch(0.55_0.25_285_/_0.25)]"
                >
                  Create Free Account
                  <ArrowRight className="size-4" />
                </Link>
              )}

              {/* Secondary line — discoverable code/promo path. Hidden in
                  pre_alpha; in alpha/beta/waitlist this surfaces the cohort
                  entry; in intro/ga it surfaces the promo entry. */}
              {(CURRENT_PHASE === 'alpha' || CURRENT_PHASE === 'beta' || CURRENT_PHASE === 'waitlist') && (
                <div className="mt-3 text-center text-xs text-on-surface-muted">
                  Have a code?{' '}
                  <Link href="/signup?from=trial" className="text-primary-bright hover:text-primary">Sign up →</Link>
                </div>
              )}
              {(CURRENT_PHASE === 'intro' || CURRENT_PHASE === 'ga') && (
                <div className="mt-3 text-center text-xs text-on-surface-muted">
                  Or{' '}
                  <Link href="/signup?from=trial" className="text-primary-bright hover:text-primary">have a promo code?</Link>
                  {' · '}
                  <Link href="/pricing#quickstart" className="text-primary-bright hover:text-primary">prepay Quickstart</Link>
                </div>
              )}
            </div>
          )}

          {/* Start over */}
          <div className="text-center">
            <button
              onClick={() => {
                setMessages([]);
                setUserMessageCount(0);
                setInput('');
                sessionIdRef.current = '';
                saveTrialState({ sessionId: '', messages: [], userMessageCount: 0 });
              }}
              className="text-xs text-on-surface-muted hover:text-on-surface-variant transition-colors"
            >
              Start a new conversation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Back-compat: legacy export name
export const HeroChat = LandingTrial;
